import {
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { DEFAULT_ARRAY_SCHEMA } from '@blaze-shared/schema-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';

import { AvailabilityDefinitionMapper } from './ai/availability.definition-mapper';
import { AvailabilityElementHandler } from './ai/availability.element-handler';
import { AvailabilityComponent } from './availability.component';
import { type AvailabilityPropsModel } from './availability.model';
import { getPropsConfig } from './availability.props-config';

export type AvailabilityDef = WithProps<AvailabilityPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    availability: AvailabilityDef;
  }
}

const DEFAULT_WIDTH = 600;
const DEFAULT_HEIGHT = 300;

registerElementDefinition({
  type: 'availability',
  componentType: AvailabilityComponent,
  isListed: true,
  availableFor: 'dev', // todo remove when the rest of the feature is ready
  name: 'Availability',
  width: { mode: 'full', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  groups: ['advanced'],
  initial: getInitial(),
  styleIncludePaths: ['style'],
  version: 1,
  ai: {
    name: 'availability',
    handler: () => AvailabilityElementHandler,
    definitionMapper: () => AvailabilityDefinitionMapper,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [{ eventType: 'change', schema: DEFAULT_ARRAY_SCHEMA }],
  };
}

function getDefaults(): PropsOf<'availability'> {
  return {
    content: {
      title: {
        enabled: true,
        align: 'left',
        label: 'Availability',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      origin: 'drag',
      options: {
        weekStart: 'Sunday',
        visibleDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        time: { from: '9:00', to: '17:00' },
      },
      input: {},
    },
    style: {
      general: {
        title: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { padding: '$t.spacingFormList' },
        fill: { color1: '$t.fillInternal', selectorType: 'none' },
        outlineWidth: '$t.containerOutlineWidth',
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        radius: '$t.radius',
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
    },
  };
}

function getInitial(): PartialPropsOf<'availability'> {
  return {};
}
